import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Search, Filter, ChevronDown, Edit2, Trash2, X, ChevronLeft, ChevronRight, MoreVertical, Hand, ShieldAlert, ShieldCheck } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useApp } from "@/contexts/AppContext";
import { getErrorMessage } from "@/utils/errorUtils";
import {USER_STATISTICS,
  USER_USERS,
  USER_SEARCH,
  USER_DELETE,
  USER_FILTER,
  USER_SAVE,
  SUSPEND_USER,
  UNSUSPEND_USER,
  UPDATE_USER_WITHDRAWAL_ACCESS,
  UserSearchParams,
  UserFilterParams
} from '../../api/services/adminservice';
import { useTranslation } from "react-i18next";

type SortOrder = "asc" | "desc";
type WithdrawalControlAction = "suspend" | "block" | "allow";
type SuspensionAction = "suspend" | "unsuspend";

interface UserApiResponse {
  data?: {
    users?: Record<string, unknown>[];
    pagination?: {
      currentPage?: number;
      totalPages?: number;
      totalItems?: number;
      total?: number;
      itemsPerPage?: number;
      limit?: number;
    };
  };
}

const getUserSortTimestamp = (user: Record<string, unknown>) => {
  const rawDate =
    user?.registrationDate ??
    user?.createdAt ??
    user?.updatedAt ??
    user?.dateJoined;

  if (!rawDate) {
    return 0;
  }

  const timestamp = new Date(rawDate as string | number).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

function initialsFromName(firstName: unknown, lastName: unknown) {
  const first = typeof firstName === 'string' ? firstName.charAt(0) : '';
  const last = typeof lastName === 'string' ? lastName.charAt(0) : '';
  return (first + last).toUpperCase() || 'U';
}

function isImageUrl(v: unknown) {
  return typeof v === 'string' && /^https?:\/\//i.test(v);
}

export default function AdminUsers() {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [fetchstatus, setFetchStatus] = useState(["All", "active", "inactive"]);
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [editmodal, setEditModal] = useState(false);
  const [editdata, seteditdata] = useState({firstName: "", lastName: "", phoneNumber: "", userId: "", location: ""});

  const { theme } = useApp();
  const isDark = theme === 'dark';
  let [displaystatus, setdisplaystatus] = useState(false);
  let [filterdisplay, setfilterdisplay] = useState(false);
  let [pagination, setpagination] = useState<unknown[]>([]);
  let [totalpage, settotalpage] = useState(1);
  let [currentpag, setcurrentpag] = useState(1);
  let [userid, setuserid] = useState(null);
  let [editloading, seteditloading] = useState(false);
  let [deleteloading, setdeleteloading] = useState(false);
  let [deletedisplay, setdeletedisplay] = useState(false);
  let [deleteform, setdeleteform] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [paginationInfo, setPaginationInfo] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  const users = [
    { id: "#12345", name: "John Smith", email: "john.smith@example.com", registrationDate: "Jan 15, 2024", status: "Active", avatar: "👤" },
    { id: "#12346", name: "Sarah Johnson", email: "sarah.j@example.com", registrationDate: "Jan 10, 2024", status: "Active", avatar: "👤" },
    { id: "#12347", name: "Michael Chen", email: "michael.chen@example.com", registrationDate: "Jan 08, 2024", status: "Inactive", avatar: "👤" },
    { id: "#12348", name: "Emma Wilson", email: "emma.wilson@example.com", registrationDate: "Jan 05, 2024", status: "Active", avatar: "👤" },
    { id: "#12349", name: "James Brown", email: "james.brown@example.com", registrationDate: "Jan 03, 2024", status: "Inactive", avatar: "👤" },
    { id: "#12350", name: "Olivia Davis", email: "olivia.davis@example.com", registrationDate: "Dec 28, 2023", status: "Active", avatar: "👤" },
  ];
function getPagination(currentPage, totalPages) {
  const MAX_VISIBLE = 9;

  const range = (start, end) =>
    Array.from({ length: end - start + 1 }, (_, i) => start + i);

  if (totalPages <= MAX_VISIBLE) {
    return range(1, totalPages);
  }

  const pages = [];
  // const leftBoundary = 4;
  // const rightBoundary = totalPages - 3;

  pages.push(...range(1, 4));

  if (currentPage > 5) {
    pages.push("...");
  }

  const middleStart = Math.max(5, currentPage - 1);
  const middleEnd = Math.min(totalPages - 4, currentPage + 1);

  pages.push(...range(middleStart, middleEnd));

  if (currentPage < totalPages - 4) {
    pages.push("...");
  }

  pages.push(...range(totalPages - 3, totalPages));

  console.log([...new Set(pages)], "pages");
   return [...new Set(pages)];
}



  
  let [userStatistics, setUserStatistics] = useState(null);
  let [usersList, setUsersList] = useState(null);
  let [usersLoading, setUsersLoading] = useState(false);
  let [searchResults, setSearchResults] = useState(null);
  let [deletedUser, setDeletedUser] = useState(null);
  let [filteredUsersList, setFilteredUsersList] = useState(null);
  let [currentsection, setCurrentSection] = useState('users');

  const sortedUsersList = useMemo(() => {
    const safeUsers = Array.isArray(usersList) ? [...usersList] : [];

    return safeUsers.sort((firstUser, secondUser) => {
      const comparison = getUserSortTimestamp(firstUser) - getUserSortTimestamp(secondUser);
      return sortOrder === "asc" ? comparison : -comparison;
    });
  }, [sortOrder, usersList]);

  const displayedUsersCount = sortedUsersList.length;
  const startResult =
    paginationInfo.totalItems === 0
      ? 0
      : (paginationInfo.currentPage - 1) * paginationInfo.itemsPerPage + 1;
  const endResult =
    paginationInfo.totalItems === 0
      ? 0
      : Math.min(
          (paginationInfo.currentPage - 1) * paginationInfo.itemsPerPage + displayedUsersCount,
          paginationInfo.totalItems
        );

  const showActionMessage = (type: "success" | "error", text: string) => {
    setActionMessage({ type, text });
    setTimeout(() => setActionMessage(null), 4000);
  };

  const getUserActionId = (user: Record<string, unknown>) => String(user?.id || user?._id || user?.userId || "");

  const applyWithdrawalStateToUser = (user: Record<string, unknown>, action: WithdrawalControlAction) => ({
    ...user,
    withdrawalStatus: action === "allow" ? "allowed" : action,
    withdrawalControl: action === "allow" ? "allowed" : action,
    withdrawalBlocked: action === "block",
    canWithdraw: action === "allow",
  });

  const applySuspensionStateToUser = (user: Record<string, unknown>, action: SuspensionAction) => ({
    ...user,
    isSuspended: action === "suspend",
    accountStatus: action === "suspend" ? "suspended" : "active",
    status: action === "suspend" ? "inactive" : "active",
    isActive: action !== "suspend",
  });
  

  
  
  async function fetchUserStatistics() {
    try {
      const data = await USER_STATISTICS();
      console.log("User Statistics:", data);
      setUserStatistics(data?.data);
    } catch (error: unknown) {
      console.error("Error fetching user statistics:", error);
    }
  }
  async function fetchUsers(reqData: Record<string, unknown> = {page: 1, limit: 10}) {
    try {
      setUsersLoading(true);
      const response = await USER_USERS(reqData);
      const resData = (response as unknown as UserApiResponse).data || {};
      const pag_ = resData?.pagination;
      const paginationArray = getPagination(
        pag_?.currentPage as number,
        pag_?.totalPages as number
      );

      setpagination(paginationArray);
      setcurrentpag(pag_?.currentPage as number);
      settotalpage(pag_?.totalPages as number);
      setPaginationInfo({
        currentPage: (pag_?.currentPage as number) || 1,
        totalPages: (pag_?.totalPages as number) || 1,
        totalItems: (pag_?.totalItems as number) || (pag_?.total as number) || resData?.users?.length || 0,
        itemsPerPage: (pag_?.itemsPerPage as number) || (pag_?.limit as number) || (reqData as { limit?: number }).limit || 10,
      });

      console.log("Users List:", response);
      setUsersList(resData?.users || []);
      setCurrentSection('users');
    } catch (error: unknown) {
      console.error("Error fetching user list:", error);
    } finally {
      setUsersLoading(false);
    }
  }

  async function fetchUserSearch(reqData: UserSearchParams) {
    try {
      setUsersLoading(true);
      const response = await USER_SEARCH(reqData);
      const resData = (response as unknown as UserApiResponse).data || {};
      console.log("User Search Results:", response);

      const spag_ = resData?.pagination;
      const paginationArray = getPagination(
        spag_?.currentPage as number,
        spag_?.totalPages as number
      );

      setpagination(paginationArray);
      setcurrentpag((spag_?.currentPage as number) || 1);
      settotalpage((spag_?.totalPages as number) || 1);
      setPaginationInfo({
        currentPage: (spag_?.currentPage as number) || 1,
        totalPages: (spag_?.totalPages as number) || 1,
        totalItems: (spag_?.totalItems as number) || (spag_?.total as number) || resData?.users?.length || 0,
        itemsPerPage: (spag_?.itemsPerPage as number) || (spag_?.limit as number) || (reqData as { limit?: number }).limit || 10,
      });

      setUsersList(resData?.users || []);
      setCurrentSection('search');

    } catch (error: unknown) {
      console.error("Error fetching user search results:", error);
    } finally {
      setUsersLoading(false);
    }
  }
  async function fetchUserDelete() {
    try {
      setUsersLoading(true);
      const response = await USER_DELETE({id: "1"});
      const resData = (response as unknown as UserApiResponse).data || {};
      console.log("User Deleted:", response);
      setUsersList(resData?.users || []);
    } catch (error: unknown) {
      console.error("Error deleting user:", error);
    } finally {
      setUsersLoading(false);
    }
  }
  async function fetchUserFilter(reqData: UserFilterParams) {
    try {
      setUsersLoading(true);
      const response = await USER_FILTER(reqData);
      const resData = (response as unknown as UserApiResponse).data || {};
      console.log("User Filtered:", resData?.users);
      
      const fpag_ = resData?.pagination;
      const paginationArray = getPagination(
        fpag_?.currentPage as number,
        fpag_?.totalPages as number
      );

      setpagination(paginationArray);
      setcurrentpag(fpag_?.currentPage as number || 1);
      settotalpage(fpag_?.totalPages as number || 1);
      setPaginationInfo({
        currentPage: fpag_?.currentPage as number || 1,
        totalPages: fpag_?.totalPages as number || 1,
        totalItems: fpag_?.totalItems as number || fpag_?.total as number || resData?.users?.length || 0,
        itemsPerPage: fpag_?.itemsPerPage as number || fpag_?.limit as number || (reqData as { limit?: number }).limit || 10,
      });

      setUsersList(resData?.users || []);
      setCurrentSection('filter');

    } catch (error: unknown) {
      console.error("Error filtering user:", error);
    } finally { 
      setUsersLoading(false);
    }
  }

  useEffect(() => {
    async function render(){
    await fetchUserStatistics();
    await fetchUsers();
    
    // await fetchUserDelete();
    
  }
  render();
}, []);

useEffect(()=>{
  async function filterrender() {
  
    if(statusFilter === "All"){
      await fetchUsers();
    } else {
await fetchUserFilter({status: statusFilter, page: 1, limit: 10});
    }
      // setUsersList(pag?.data?.users);

  }

  filterrender();
}, [statusFilter]);

  useEffect(()=>{
    const trimmedSearch = searchTerm.trim();
    if (trimmedSearch.length === 0) {
      if (currentsection === 'search') {
        setcurrentpag(1);

        if (statusFilter === "All") {
          fetchUsers({page: 1, limit: 10});
        } else {
          fetchUserFilter({status: statusFilter, page: 1, limit: 10});
        }
      }

      return;
    }

    setcurrentpag(1);
    fetchUserSearch({search: trimmedSearch, page: 1, limit: 10});
  }, [searchTerm]);

  useEffect(()=>{
    const paginationArray = getPagination(
       currentpag,
       totalpage
     );
     if(currentsection === 'users'){
      fetchUsers({page: currentpag, limit: 10});
     }
     if(currentsection === 'search'){
      fetchUserSearch({search: searchTerm.trim(), page: currentpag, limit: 10});
     }
     if(currentsection === 'filter'){
      fetchUserFilter({status: statusFilter, page: currentpag, limit: 10});
     }


     setpagination(paginationArray);
     console.log(currentpag,
       totalpage);
  },[currentpag]);

  async function handleSaveEdit(edit){
    try{
    console.log(edit, "editdata");
      seteditloading(true);

    let save = await USER_SAVE(userid, editdata);
    fetchUsers({page: currentpag, limit: 10});
    console.log(save, "savedata");
    } catch (err: unknown){
      console.log(err, "error");
    } finally{
      seteditloading(false);
    }
  }

  async function handleDeleteUser(){
    // Require the exact confirmation phrase. Show an inline error (not an alert)
    // and keep the modal open so the user can correct it.
    if(deleteform.trim() !== "DELETE THIS USER"){
      setDeleteError(t("admin.users.modals.delete.mismatch", { defaultValue: 'To delete this user, type exactly: DELETE THIS USER' }));
      return;
    }
    if(!userid){
      setDeleteError(t("admin.users.actions.invalidUser", { defaultValue: "Unable to delete this user." }));
      return;
    }
    try{
      setDeleteError("");
      setdeleteloading(true);
      await USER_DELETE({ id: String(userid) });
      showActionMessage("success", t("admin.users.modals.delete.success", { defaultValue: "User deleted." }));
      setdeletedisplay(false);
      setdeleteform("");
      await fetchUsers({page: currentpag, limit: 10});
    } catch (err: unknown){
      console.error("Error deleting user:", err);
      showActionMessage("error", getErrorMessage(err) || t("admin.users.modals.delete.failed", { defaultValue: "Failed to delete user." }));
    } finally{
      setdeleteloading(false);
    }
  }

  async function handleWithdrawalControl(user: Record<string, unknown>, action: WithdrawalControlAction) {
    const targetId = getUserActionId(user);
    if (!targetId) {
      showActionMessage("error", t("admin.users.actions.invalidUser", { defaultValue: "Unable to update this user." }));
      return;
    }

    try {
      setActionLoadingId(targetId);
      await UPDATE_USER_WITHDRAWAL_ACCESS(
        targetId,
        action === "block"
          ? { blocked: true, reason: "Fraud review" }
          : { blocked: false }
      );

      setUsersList((currentUsers: Record<string, unknown>[]) =>
        Array.isArray(currentUsers)
          ? currentUsers.map((currentUser) =>
              getUserActionId(currentUser) === targetId
                ? applyWithdrawalStateToUser(currentUser, action)
                : currentUser
            )
          : currentUsers
      );

      const successMessage =
        action === "suspend"
          ? t("admin.users.actions.withdrawalSuspended", { defaultValue: "Withdrawal access suspended." })
          : action === "block"
            ? t("admin.users.actions.withdrawalBlocked", { defaultValue: "Withdrawal access blocked." })
            : t("admin.users.actions.withdrawalAllowed", { defaultValue: "Withdrawal access restored." });

      showActionMessage("success", successMessage);
    } catch (error: unknown) {
      console.error("Error updating withdrawal access:", error);
      showActionMessage("error", t("admin.users.actions.updateFailed", { defaultValue: "Failed to update withdrawal access." }));
    } finally {
      setActionLoadingId(null);
    }
  }

  async function handleSuspensionControl(user: Record<string, unknown>, action: SuspensionAction) {
    const targetId = getUserActionId(user);
    if (!targetId) {
      showActionMessage("error", t("admin.users.actions.invalidUser", { defaultValue: "Unable to update this user." }));
      return;
    }

    try {
      setActionLoadingId(targetId);
      if (action === "suspend") {
        await SUSPEND_USER(targetId, "Violation of policy");
      } else {
        await UNSUSPEND_USER(targetId);
      }

      setUsersList((currentUsers: Record<string, unknown>[]) =>
        Array.isArray(currentUsers)
          ? currentUsers.map((currentUser) =>
              getUserActionId(currentUser) === targetId
                ? applySuspensionStateToUser(currentUser, action)
                : currentUser
            )
          : currentUsers
      );

      showActionMessage(
        "success",
        action === "suspend"
          ? t("admin.users.actions.userSuspended", { defaultValue: "User suspended successfully." })
          : t("admin.users.actions.userUnsuspended", { defaultValue: "User unsuspended successfully." })
      );
    } catch (error: unknown) {
      console.error("Error updating suspension status:", error);
      showActionMessage("error", t("admin.users.actions.updateFailed", { defaultValue: "Failed to update withdrawal access." }));
    } finally {
      setActionLoadingId(null);
    }
  }

  return (
    <div className="space-y-6">




       {/* User Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 ">
        <Card className="p-6 dark:bg-[#333436] dark:border-[#333436]">
          <h4 className="text-foreground/60 text-sm mb-2">{t("admin.users.stats.totalUsers", { defaultValue: "Total Users" })}</h4>
          <p className="text-3xl font-bold text-foreground">{userStatistics?.totalUsers || 0}</p>
        </Card>

        <Card className="p-6 dark:bg-[#333436] dark:border-[#333436]">
          <h4 className="text-foreground/60 text-sm mb-2">{t("admin.users.stats.activeUsers", { defaultValue: "Active Users" })}</h4>
          <p className="text-3xl font-bold text-foreground text-green-600">{userStatistics?.activeUsers  || 0}</p>
        </Card>

        <Card className="p-6 dark:bg-[#333436] dark:border-[#333436]">
          <h4 className="text-foreground/60 text-sm mb-2">{t("admin.users.stats.totalEarnings", { defaultValue: "Total Earnings" })}</h4>
          <p className="text-3xl font-bold text-foreground text-red-600">${userStatistics?.totalEarnings || 0}</p>
        </Card>
      </div>

      {actionMessage && (
        <div
          className={`rounded-lg border px-4 py-3 text-sm ${
            actionMessage.type === "success"
              ? isDark
                ? "border-green-800 bg-green-950 text-green-300"
                : "border-green-200 bg-green-50 text-green-700"
              : isDark
                ? "border-red-800 bg-red-950 text-red-300"
                : "border-red-200 bg-red-50 text-red-700"
          }`}
        >
          {actionMessage.text}
        </div>
      )}
      
      {/* Search and Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search Input */}
        <div className="flex-1 relative">
          <Search className={`absolute left-4 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} size={20} />
          <input
            type="text"
            placeholder={t("admin.users.search.placeholder", { defaultValue: "Search users..." })}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`w-full pl-12 pr-4 py-3 rounded-lg border ${
              isDark 
                ? 'bg-black border-[#2b2b2c] text-white placeholder-gray-500' 
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
            } focus:outline-none focus:ring-2 focus:ring-blue-500 transition`}
          />
        </div>

        {/* Status Filter Dropdown */}
        <div className="relative">
          <button
            className={`px-6 py-3 rounded-lg border flex items-center gap-2 min-w-[140px] ${
              isDark 
                ? 'bg-black border-blue-600 text-blue-600 hover:bg-gray-700' 
                : 'bg-white border-black text-gray-900 hover:bg-gray-50'
            } transition`}
            onClick={() => setdisplaystatus(!displaystatus)}
          >
            <span>{statusFilter === "All" ? t("admin.common.all", { defaultValue: "All" }) : statusFilter === "active" ? t("admin.common.active", { defaultValue: "Active" }) : t("admin.common.inactive", { defaultValue: "Inactive" })}</span>
            <ChevronDown size={16} />
          </button>
        {displaystatus && <div className="absolute right-0 top-[100%] mt-2 h-max w-full bg-red-500">
              {fetchstatus.map((status) => (
            <div className={`min-h-[30px] bg-white dark:bg-[#333436] cursor-pointer shadow-lg overflow-hidden flex  items-center pl-4 ${statusFilter === status ? 'text-blue-600' : ''}`}
              onClick={() => {
                setStatusFilter(status);
                setdisplaystatus(false);
              }}
            >
              {status === "All" ? t("admin.common.all", { defaultValue: "All" }) : status === "active" ? t("admin.common.active", { defaultValue: "Active" }) : t("admin.common.inactive", { defaultValue: "Inactive" })}
            </div>))}
        </div>}
        </div>

        {/* Filter Button */}
        <div className="relative">
        <button
          className="px-6 py-3 bg-black dark:bg-blue-600 text-white dark:text-black rounded-lg flex items-center gap-2 font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition"
          onClick={() => setfilterdisplay(!filterdisplay)}
        >
          <Filter size={18} />
          <span>
            {t("admin.users.filter.button", { defaultValue: "Filter" })}:{" "}
            {sortOrder === "asc"
              ? t("admin.users.filter.ascending", { defaultValue: "Ascending" })
              : t("admin.users.filter.descending", { defaultValue: "Descending" })}
          </span>
        </button>
        {filterdisplay && <div className={`absolute right-0 top-[100%] z-10 mt-2 min-w-[190px] overflow-hidden rounded-lg border shadow-lg ${
          isDark ? 'border-[#2b2b2c] bg-[#333436]' : 'border-gray-200 bg-white'
        }`}>
            <button
              type="button"
              className={`flex min-h-[42px] w-full items-center justify-between px-4 text-left text-sm transition ${
                sortOrder === "asc"
                  ? 'text-blue-600'
                  : isDark
                    ? 'text-gray-200 hover:bg-black'
                    : 'text-gray-700 hover:bg-gray-50'
              }`}
              onClick={() => {
                setSortOrder("asc");
                setfilterdisplay(false);
              }}
            >
              <span>{t("admin.users.filter.ascending", { defaultValue: "Ascending" })}</span>
              <span>{sortOrder === "asc" ? "✓" : ""}</span>
            </button>
            <button
              type="button"
              className={`flex min-h-[42px] w-full items-center justify-between px-4 text-left text-sm transition ${
                sortOrder === "desc"
                  ? 'text-blue-600'
                  : isDark
                    ? 'text-gray-200 hover:bg-black'
                    : 'text-gray-700 hover:bg-gray-50'
              }`}
              onClick={() => {
                setSortOrder("desc");
                setfilterdisplay(false);
              }}
            >
              <span>{t("admin.users.filter.descending", { defaultValue: "Descending" })}</span>
              <span>{sortOrder === "desc" ? "✓" : ""}</span>
            </button>
        </div>}
      </div>

      </div>

      {/* Users Table */}
      <div className={`${isDark ? 'bg-[#333436] border-[#2b2b2c]' : 'bg-white border-gray-200'} rounded-lg border overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`${isDark ? 'bg-black border-black' : 'bg-gray-50 border-gray-200'} border-b`}>
              <tr>
                <th className={`text-left py-4 px-6 font-semibold text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t("admin.users.table.userId", { defaultValue: "User ID" })}
                </th>
                <th className={`text-left py-4 px-6 font-semibold text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t("admin.users.table.name", { defaultValue: "Name" })}
                </th>
                <th className={`text-left py-4 px-6 font-semibold text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t("admin.users.table.email", { defaultValue: "Email" })}
                </th>
                <th className={`text-left py-4 px-6 font-semibold text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t("admin.users.table.registrationDate", { defaultValue: "Registration Date" })}
                </th>
                <th className={`text-left py-4 px-6 font-semibold text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t("admin.users.table.status", { defaultValue: "Status" })}
                </th>
                <th className={`text-left py-4 px-6 font-semibold text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t("admin.users.table.actions", { defaultValue: "Actions" })}
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-[#2b2b2c]' : 'divide-gray-200'}`}>
              {usersLoading && (
                <tr>
                  <td colSpan={6} className="py-6 text-center"> 
                    <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>{t("admin.users.loadingUsers", { defaultValue: "Loading users..." })}</p>
                  </td>
                </tr>
              )}
              {usersLoading === false && sortedUsersList.map((user) => (
                <tr key={user.id} className={`${isDark ? 'hover:bg-gray-800 hover:bg-opacity-30' : 'hover:bg-gray-50'} transition`}>
                  <td className={`py-4 px-6 text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {user.userId}
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-3">
                      <div className={`relative w-8 h-8 sm:w-10 sm:h-10 shrink-0 overflow-hidden rounded-full flex items-center justify-center text-sm sm:text-lg font-semibold ${
                        isDark ? 'bg-gray-700 text-gray-300' : 'bg-blue-100 text-blue-600'
                      }`}>
                        <span className="absolute inset-0 flex items-center justify-center">
                          {!isImageUrl(user.avatar) && user.avatar ? (user.avatar as string) : initialsFromName(user.firstName, user.lastName)}
                        </span>
                        {isImageUrl(user.avatar) && (
                          <img
                            src={user.avatar as string}
                            alt={`${user.firstName || ''} ${user.lastName || ''}`.trim() || 'User'}
                            className="absolute inset-0 w-full h-full object-cover z-10"
                            onError={(ev)=>{
                              ev.currentTarget.style.display = 'none';
                            }}
                          />
                        )}
                      </div>
                      <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {user.firstName} {user.lastName}
                      </span>
                    </div>
                  </td>
                  <td className={`py-4 px-6 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {user.email}
                  </td>
                  <td className={`py-4 px-6 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {user.registrationDate || user.createdAt
                      ? new Date(user.registrationDate || user.createdAt).toLocaleDateString()
                      : 'N/A'}
                  </td>
                  <td className="py-4 px-6">
                    <span className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-medium ${
                      (user.status || '').toLowerCase() === 'active'
                        ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:bg-opacity-30 dark:text-green-400'
                        : 'bg-red-100 text-red-700 dark:bg-red-900 dark:bg-opacity-30 dark:text-red-400'
                    }`}>
                      {user.status ? user.status.charAt(0).toUpperCase() + user.status.slice(1) : ''}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-2">
                      <button onClick={()=>{
                        setEditModal(true);
                        setuserid(getUserActionId(user));
                        seteditdata({
                          firstName: user.firstName,
                          lastName: user.lastName,
                          phoneNumber: user.phoneNumber,
                          userId: user.userId,
                          location: user.location
                        });
                      }} className={`p-2 rounded hover:bg-opacity-10 transition ${
                        isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'
                      }`}>
                        <Edit2 size={16} />
                      </button>
                      <button onClick={()=>{
                        setdeletedisplay(true);
                        setuserid(getUserActionId(user));
                        setdeleteform("");
                        setDeleteError("");
                      }} className="p-2 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900 dark:hover:bg-opacity-20 transition">
                        <Trash2 size={16} />
                      </button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            disabled={actionLoadingId === getUserActionId(user)}
                            className={`p-2 rounded transition ${
                              isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'
                            } disabled:opacity-50`}
                            aria-label={t("admin.users.actions.more", { defaultValue: "More actions" })}
                          >
                            <MoreVertical size={16} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className={isDark ? 'border-[#2b2b2c] bg-[#333436] text-gray-100' : 'border-gray-200 bg-white text-gray-900'}
                        >
                          {user.isSuspended ? (
                            <DropdownMenuItem onClick={() => handleSuspensionControl(user, "unsuspend")}>
                              <ShieldCheck className="mr-2 h-4 w-4" />
                              {t("admin.users.actions.unsuspend", { defaultValue: "Unsuspend user" })}
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => handleSuspensionControl(user, "suspend")}>
                              <Hand className="mr-2 h-4 w-4" />
                              {t("admin.users.actions.suspend", { defaultValue: "Suspend user" })}
                            </DropdownMenuItem>
                          )}
                          {user.withdrawalBlocked ? (
                            <DropdownMenuItem onClick={() => handleWithdrawalControl(user, "allow")}>
                              <ShieldCheck className="mr-2 h-4 w-4" />
                              {t("admin.users.actions.allowWithdrawal", { defaultValue: "Allow withdrawal" })}
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => handleWithdrawalControl(user, "block")}>
                              <ShieldAlert className="mr-2 h-4 w-4" />
                              {t("admin.users.actions.blockWithdrawal", { defaultValue: "Block withdrawal" })}
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </td>
                </tr>
              ))}
              {usersLoading === false && sortedUsersList.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-6 text-center">
                    <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>{t("admin.users.noUsersFound", { defaultValue: "No users found." })}</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className={`flex items-center justify-between px-6 py-4 border-t ${isDark ? 'bg-black' : 'border-gray-200'}`}>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setcurrentpag((page) => Math.max(1, page - 1))}
              disabled={currentpag === 1}
              className={`px-4 py-2 rounded border font-medium transition ${
                isDark 
                  ? 'border-[#2b2b2c] text-gray-400 hover:bg-gray-800 disabled:opacity-50' 
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50'
              }`}
            >
              {t("admin.common.previous", { defaultValue: "Previous" })}
            </button>
            <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {t("admin.users.pagination.resultsRange", { defaultValue: "{{start}} to {{end}} of {{total}} results", start: startResult, end: endResult, total: paginationInfo.totalItems })}
            </span>
          </div>

         <div className="flex items-center gap-2">
            {pagination?.length !== 0 && <button
              onClick={() => setcurrentpag((page) => Math.max(1, page - 1))}
              disabled={currentpag === 1}
              className={`w-8 h-8 flex items-center justify-center rounded border transition ${
                isDark 
                  ? 'border-[#2b2b2c] text-gray-400 hover:bg-gray-800 disabled:opacity-50' 
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50'
              }`}
            >
              <ChevronLeft size={16} />
            </button>}

            {pagination?.map(page=> (
              <button
                key={String(page)}
                onClick={() => setcurrentpag((p)=> typeof page == "number" ? page  : p)}
                className={`w-8 h-8 flex items-center justify-center rounded font-medium transition ${
                  currentpag === page
                    ? 'dark:bg-blue-600 bg-black text-white'
                    : isDark 
                      ? 'border border-[#2b2b2c] text-gray-400 hover:bg-gray-800' 
                      : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {String(page)}
              </button>
            ))}
            
            {pagination?.length !== 0 && <button
              onClick={() => setcurrentpag((p)=> p < totalpage ? p + 1 : p)}
              disabled={currentpag === totalpage}
              className={`w-8 h-8 flex items-center justify-center rounded border transition ${
                isDark 
                  ? 'border-[#2b2b2c] text-gray-400 hover:bg-gray-800 disabled:opacity-50' 
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50'
              }`}
            >
              <ChevronRight size={16} />
            </button>}
          </div>
        </div>
      </div>

      {/* {filteredUsers.length === 0 && (
        <div className="text-center py-12">
          <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            No users found matching your search
          </p>
        </div>
      )} */}
      {editmodal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => setEditModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className={`w-full max-w-md max-h-[90vh] overflow-hidden rounded-2xl border shadow-2xl ${
              theme === 'dark' ? 'bg-[#1f2022] border-gray-700' : 'bg-white border-gray-200'
            }`}
          >
            <div className={`flex items-center justify-between px-6 py-4 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {t("admin.users.modals.edit.title", { defaultValue: "Edit User" })}
              </h2>
              <button
                onClick={() => setEditModal(false)}
                className={`p-1.5 rounded-lg transition ${theme === 'dark' ? 'text-gray-400 hover:bg-gray-700 hover:text-white' : 'text-gray-500 hover:bg-gray-100'}`}
              >
                <X size={18} />
              </button>
            </div>
            <div className="px-6 py-5 overflow-y-auto max-h-[calc(90vh-140px)]">

      
                <div className="space-y-6">
                 <div className="w-full overflow-y-scroll p-1">
                      <div className="mb-2">
                              <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                {t("admin.users.modals.edit.firstName", { defaultValue: "First Name" })}
                              </label>
                            <div className="relative">
                              <input
                                type={"text"}
                                value={editdata.firstName}
                                onChange={(e) => {
                                  console.log(e.target.value);
                                  seteditdata({...editdata, firstName: e.target.value});
                                  console.log(editdata.firstName, 'currently');
                                }}
                                className={`w-full px-4 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                  theme === 'dark'
                                    ? 'bg-black border-gray-700 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                                } border`}
                              />
                          </div>
                      </div>
          
                      <div className="mb-2">
                              <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                {t("admin.users.modals.edit.lastName", { defaultValue: "Last Name" })}
                              </label>
                              <div className="relative">
                              <input
                                type={ "text"}
                                value={editdata.lastName}
                                onChange={(e) => seteditdata({...editdata, lastName: e.target.value})}
                                className={`w-full px-4 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                  theme === 'dark'
                                    ? 'bg-black border-gray-700 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                                } border`}
                              />
                              
                          </div>
                      </div>
                      <div className="mb-2">
                              <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                {t("admin.users.modals.edit.phoneNo", { defaultValue: "Phone Number" })}
                              </label>
                              <div className="relative">
                              <input
                                type={ "text"}
                                value={editdata.phoneNumber}
                                onChange={(e) => seteditdata({...editdata, phoneNumber: e.target.value})}
                                className={`w-full px-4 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                  theme === 'dark'
                                    ? 'bg-black border-gray-700 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                                } border`}
                              />
                              
                          </div>
                      </div>
                      <div className="mb-2">
                              <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                {t("admin.users.modals.edit.location", { defaultValue: "Location" })}
                              </label>
                              <div className="relative">
                              <input
                                type={ "text"}
                                value={editdata.location}
                                onChange={(e) => seteditdata({...editdata, location: e.target.value})}
                                className={`w-full px-4 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                  theme === 'dark'
                                    ? 'bg-black border-gray-700 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                                } border`}
                              />
                              
                          </div>
                      </div>
                  </div>
                  </div>
            </div>
            <div className={`flex items-center justify-end gap-3 px-6 py-4 border-t ${theme === 'dark' ? 'border-gray-700 bg-[#1f2022]' : 'border-gray-200 bg-gray-50'}`}>
              <button
                onClick={() => setEditModal(false)}
                className={`px-4 py-2.5 rounded-lg font-medium transition-colors ${theme === 'dark' ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-200'}`}
              >
                {t("admin.users.modals.edit.cancel", { defaultValue: "Cancel" })}
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-6 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                disabled={editloading}
              >
                {editloading ? t("admin.users.modals.edit.saving", { defaultValue: "Saving..." }) : t("admin.users.modals.edit.save", { defaultValue: "Save Changes" })}
              </button>
            </div>
          </div>
        </div>
      )}
      {deletedisplay && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => setdeletedisplay(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className={`w-full max-w-md overflow-hidden rounded-2xl border shadow-2xl ${
              theme === 'dark' ? 'bg-[#1f2022] border-gray-700' : 'bg-white border-gray-200'
            }`}
          >
            <div className={`flex items-center justify-between px-6 py-4 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className="text-lg font-semibold text-red-600">
                {t("admin.users.modals.delete.title", { defaultValue: "Delete User" })}
              </h2>
              <button
                onClick={() => setdeletedisplay(false)}
                className={`p-1.5 rounded-lg transition ${theme === 'dark' ? 'text-gray-400 hover:bg-gray-700 hover:text-white' : 'text-gray-500 hover:bg-gray-100'}`}
              >
                <X size={18} />
              </button>
            </div>
            <div className="px-6 py-5">
              <div className={`flex items-start gap-3 mb-4 p-3 rounded-lg ${theme === 'dark' ? 'bg-red-900/20 border border-red-900/40' : 'bg-red-50 border border-red-200'}`}>
                <ShieldAlert size={18} className="text-red-500 mt-0.5 shrink-0" />
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t("admin.users.modals.delete.helper", { defaultValue: "This action cannot be undone." })}
                </p>
              </div>
              <label className={`block text-sm mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                {t("admin.users.modals.delete.instructionPrefix", { defaultValue: "To confirm, type" })}{" "}
                <span className="font-mono font-bold text-red-600 select-all">DELETE THIS USER</span>{" "}
                {t("admin.users.modals.delete.instructionSuffix", { defaultValue: "in the box below." })}
              </label>
              <input
                type="text"
                value={deleteform}
                placeholder={t("admin.users.modals.delete.placeholder", { defaultValue: "DELETE THIS USER" })}
                onChange={(e) => { setdeleteform(e.target.value); if (deleteError) setDeleteError(""); }}
                className={`w-full px-4 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 border ${
                  deleteError
                    ? 'border-red-500'
                    : theme === 'dark' ? 'bg-black border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
                } ${theme === 'dark' ? 'bg-black text-white' : 'bg-white text-gray-900'}`}
              />
              {deleteError && (
                <p className="mt-2 text-sm text-red-600">{deleteError}</p>
              )}
            </div>
            <div className={`flex items-center justify-end gap-3 px-6 py-4 border-t ${theme === 'dark' ? 'border-gray-700 bg-[#1f2022]' : 'border-gray-200 bg-gray-50'}`}>
              <button
                onClick={() => setdeletedisplay(false)}
                className={`px-4 py-2.5 rounded-lg font-medium transition-colors ${theme === 'dark' ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-200'}`}
              >
                {t("admin.users.modals.delete.cancel", { defaultValue: "Cancel" })}
              </button>
              <button
                onClick={handleDeleteUser}
                className="px-6 py-2.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                disabled={deleteloading}
              >
                {deleteloading ? t("admin.users.modals.delete.deleting", { defaultValue: "Deleting..." }) : t("admin.users.modals.delete.button", { defaultValue: "Delete user" })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
